import mixpanel from 'mixpanel-browser';
import { gtmTrack } from './tracking';

/*
 * Diagnostic events — instrument the moments a user gets STUCK or turns around,
 * not only the moments they succeed.
 *
 * The event dictionary covers the happy paths well. What it could not tell us is
 * that /admin/visuals-review rendered an error AND a false "No candidates
 * awaiting review" empty state at the same time — for three weeks (QUI#586). A
 * user hit a dead end on every visit and nothing fired. The page looked alive in
 * every success-shaped metric we had, because a page that never loads never
 * reaches the events that would have shown it.
 *
 * The rule: a surface that shows a user nothing must say so.
 *
 * DESIGN CONSTRAINTS (each one is load-bearing)
 *
 *  - DEDUPED per surface+state, for the lifetime of the PAGE (not per mount —
 *    the latch is module state, so navigating away and back does not re-emit an
 *    unchanged state). React re-renders an error branch freely; without a latch
 *    a single broken page emits hundreds of identical events, which is both
 *    expensive and useless — the signal we want is "this surface reached this
 *    state", not how many times it repainted. Transitions always emit, so a
 *    retry that succeeds, or a surface that breaks later in the session, is
 *    still reported.
 *  - NEVER THROWS. This instrument fires on the error path by definition. An
 *    instrument that can break the page it is reporting on is worse than none:
 *    it converts a visible empty state into a white screen.
 *  - NO CONTENT, NO PII. Surface name, state, and a short reason CODE only —
 *    never a message string, question text, or user input. Error messages get
 *    edited to include specifics over time, so allowing them would leak content
 *    eventually even if today's are clean. Same posture as the session-replay
 *    suppression on the content surfaces.
 *
 * FOLLOW-ON WIRING (deliberately not in this change — each needs its own care)
 *  - Quiz configuration abandoned: started but never launched, and on which
 *    step. Needs page-level lifecycle, not a render-time state.
 *  - Time to first question for a new account. Needs a signup timestamp the
 *    client does not hold today.
 */

/** The state a surface resolved to for the user. Mutually exclusive by design —
 *  the #586 bug was two of these being true at once. */
export const SURFACE_STATE = Object.freeze({
  /** The surface failed to load. A dead end. */
  ERROR: 'error',
  /** The surface loaded and legitimately has nothing to show. */
  EMPTY: 'empty',
  /** The surface loaded with content. The baseline every rate needs. */
  LOADED: 'loaded',
});

export const SURFACE_STATE_EVENT = 'surface_state';

// Keyed, not a single boolean. One shared flag would let the ad-blocked-Mixpanel
// cohort — a NORMAL production state — trip it on the first call, after which a
// genuinely NEW failure elsewhere logs nothing, ever. Keyed by sink and error
// name: the flood stays suppressed, a different failure still gets through once.
const sinkFailuresLogged = new Set();
const noteSinkFailure = (what, error) => {
  const key = `${what}:${error?.name || 'Error'}`;
  if (sinkFailuresLogged.has(key)) return;
  sinkFailuresLogged.add(key);
  console.debug(`${what} skipped (further occurrences silenced):`, error?.message);
};

/** Test seam — module state has to be resettable between cases. */
export const __resetSinkFailureLog = () => { sinkFailuresLogged.clear(); };

// One latch per surface PER SINK, holding the last state that sink accepted.
//
// A single shared latch cannot satisfy both constraints at once, and both are
// real. Latch before emitting and a throwing sink is deduped forever, so an
// ad-blocked Mixpanel silently kills the state=error alert. Latch only after
// every sink succeeds and the WORKING sink re-receives the same event on every
// dependency change, so the DEDUPED constraint stops holding for GTM — which is
// the one still delivering. Latch on "at least one landed" and the failing sink
// never reconciles when it recovers.
//
// Per-sink is the only shape with all three properties: each sink dedupes
// independently, a recovered sink catches up, and a total failure stays
// retryable. Surface-keyed within each, so a TRANSITION (empty -> loaded after a
// retry) always re-emits while a repeated identical state stays silent.
const lastReported = { gtm: new Map(), mixpanel: new Map() };

// The state a surface last successfully reported to ANY sink — used to let a
// recovery leave a state on the same precondition that entered it.
const lastReportedState = (surface) =>
  lastReported.gtm.get(surface) ?? lastReported.mixpanel.get(surface);

/** Test seam — module state has to be resettable between cases. */
export const __resetSurfaceStates = () => {
  lastReported.gtm.clear();
  lastReported.mixpanel.clear();
};

/**
 * Report the state a surface resolved to, once per distinct state.
 *
 * @param {string} surface Stable surface id, e.g. 'review_queue'. Not a route:
 *   routes change with navigation redesigns, and a metric that breaks when the
 *   URL moves is a metric nobody trusts.
 * @param {string} state One of SURFACE_STATE.
 * @param {object} [detail] Small, non-identifying extras. `reason` should be a
 *   short CODE ('load_failed', 'not_enabled'), never a message.
 * @returns {boolean} true when an event was emitted, false when deduped or
 *   rejected. Returned for tests and callers that want to assert; never thrown.
 */
export const trackSurfaceState = (surface, state, detail = {}) => {
  try {
    if (!surface || typeof surface !== 'string') return false;
    if (!Object.values(SURFACE_STATE).includes(state)) return false;
    // Nothing to do only when EVERY sink already has this state.
    if (lastReported.gtm.get(surface) === state && lastReported.mixpanel.get(surface) === state) {
      return false;
    }

    // Identity keys last: spreading detail last let a caller overwrite `surface`
    // and `state`, so the event reported a state the surface was not in WHILE the
    // latch recorded the real one — dropping the next genuine occurrence. A
    // module whose whole purpose is catching that must not be able to produce it.
    const payload = { ...sanitizeDetail(detail), surface, state };

    let delivered = false;
    if (lastReported.gtm.get(surface) !== state) {
      try {
        gtmTrack(SURFACE_STATE_EVENT, { event_category: 'Diagnostics', ...payload });
        lastReported.gtm.set(surface, state);
        delivered = true;
      } catch (gtmError) {
        noteSinkFailure('Surface state tracking (gtm)', gtmError);
      }
    }
    if (lastReported.mixpanel.get(surface) !== state) {
      try {
        mixpanel.track(SURFACE_STATE_EVENT, payload);
        lastReported.mixpanel.set(surface, state);
        delivered = true;
      } catch (mixpanelError) {
        noteSinkFailure('Surface state tracking (mixpanel)', mixpanelError);
      }
    }
    return delivered;
  } catch (error) {
    // Backstop. The sinks are individually wrapped above, so this only catches a
    // failure in the guards or the payload build — but NEVER THROWS is
    // load-bearing here (this runs on the error path), so it stays.
    noteSinkFailure('Surface state tracking', error);
    return false;
  }
};

// Cap for the one free-text-ish field that is allowed through.
const MAX_REASON_LENGTH = 40;
// snake_case words only, and it must START with a letter. Deliberately narrow:
// a permissive character class still admitted a UUID (lowercase hex + hyphens,
// 36 chars) and a Mongo ObjectId, which is how the previous shape check leaked.
// This admits load_failed / not_enabled and rejects every identifier and every
// scrap of prose — no @, no spaces, no capitals, no hyphens, no leading digit.
const REASON_CODE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

// ALLOWLIST, not a length cap. The cap was mechanically correct and bought the
// wrong property: a 40-char limit passes virtually every real email address, a
// UUID, a Mongo ObjectId and a person's name intact — and a re-gate probe
// reassembled a 137-char question stem byte-for-byte by chunking it across four
// arbitrary keys, because the cap is per-value with no key bound. If the caller
// is the thing being defended against, only known-safe keys may travel.
const ALLOWED_DETAIL = {
  // Short machine code, e.g. 'load_failed', 'not_enabled'. Still capped, because
  // reason codes accrete specifics over time.
  // SHAPE, not just length. A 40-char cap comfortably fits an email, a UUID and
  // a Mongo ObjectId — a re-gate rode all three through it verbatim. A reason is
  // a machine code; anything with an @, spaces, or capitals is prose or an
  // identifier and is dropped rather than truncated into something plausible.
  reason: (v) =>
    (typeof v === 'string' && v.length <= MAX_REASON_LENGTH && REASON_CODE.test(v) ? v : undefined),
  // Bounded: a raw id (phone, user id) is a big number, a diagnostic count is not.
  count: (v) => (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 100000 ? v : undefined),
  retried: (v) => (typeof v === 'boolean' ? v : undefined),
};

const sanitizeDetail = (detail) => {
  const clean = {};
  // An ARRAY passed as detail is not a detail object: typeof [] === 'object'
  // would let it through and Object.entries would yield index keys.
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return clean;
  Object.entries(ALLOWED_DETAIL).forEach(([key, coerce]) => {
    // Own properties only: `Object.create({ reason: 'x@y.com' })` inherited
    // straight through the allowlist.
    if (!Object.prototype.hasOwnProperty.call(detail, key)) return;
    if (detail[key] == null) return;
    const value = coerce(detail[key]);
    if (value !== undefined) clean[key] = value;
  });
  return clean;
};

/*
 * Resolve the state a surface is in, as an ORDERED RULE TABLE rather than a
 * chain of ifs.
 *
 * This shape is deliberate and was earned. Four adversarial review rounds found
 * roughly a dozen defects in this function and its caller, and every one of them
 * was a PRECEDENCE bug — a branch moved, or added, that silently changed what an
 * earlier branch shadowed. Round 3 added a recovery branch that was correct in
 * isolation and wrong in POSITION: sitting above the `disabled` rule, it made a
 * failed load that retried into a disabled surface report `loaded` while the
 * user read "Image review is not enabled yet". That is precisely the #586 lie
 * this module exists to catch, produced by the module itself.
 *
 * As a table, order is data you can read top to bottom and diff, the rules are
 * exhaustive, and adding a case cannot silently reorder the others.
 *
 * Read it as: the first rule whose `when` holds decides the state. `null` means
 * report nothing.
 */
const RESOLUTION_RULES = [
  // Nothing is knowable yet. `settled` is the precondition and `!loading` is NOT
  // a substitute: on mount `loading` is false because the fetch effect's
  // setState lands in the NEXT render, so the surface read as "resolved and
  // empty" before a byte was fetched. Fails CLOSED — an omitted flag reports
  // nothing, because the next consumer to wire this module without one would
  // otherwise ship that defect verbatim.
  { when: (f) => f.settled !== true && f.errorSettled !== true, state: null },
  { when: (f) => f.loading === true, state: null },

  // An error is knowable the instant the PRIMARY feed settles. Gating it behind
  // supplementary feeds meant one hung best-effort request suppressed the
  // dead-end alert entirely.
  { when: (f) => !!f.error && f.errorSettled === true, state: SURFACE_STATE.ERROR, reason: 'load_failed' },
  { when: (f) => !!f.error, state: null },

  // Disabled outranks both emptiness and recovery: the user is shown nothing,
  // whatever some independently-fetched rail still holds.
  { when: (f) => !!f.disabled && f.errorSettled === true, state: SURFACE_STATE.EMPTY, reason: 'not_enabled' },
  { when: (f) => !!f.disabled, state: null },

  // Recovery leaves `error` on the same precondition that admitted it —
  // otherwise a state could be entered on a weaker gate than anything could
  // leave it on, latching the surface in a stuck error for the page's life.
  { when: (f) => f.wasError && f.errorSettled === true && f.isEmpty === true, state: SURFACE_STATE.EMPTY },
  { when: (f) => f.wasError && f.errorSettled === true, state: SURFACE_STATE.LOADED },

  // Ordinary resolution needs every feed settled, so a slower supplementary
  // feed cannot produce a transient empty->loaded flicker.
  { when: (f) => f.settled !== true, state: null },
  { when: (f) => f.isEmpty === true, state: SURFACE_STATE.EMPTY },
  { when: () => true, state: SURFACE_STATE.LOADED },
];

/** Pure: the rule that fires for a set of flags. Exported for exhaustive tests. */
export const resolveSurfaceState = (flags = {}) =>
  RESOLUTION_RULES.find((rule) => rule.when(flags)) || { state: null };

/**
 * Convenience for the common render-time shape: derive the state from the flags
 * a surface already computes, so a caller does not hand-roll the precedence and
 * risk reporting two states — the exact #586 defect, where an error and an empty
 * state rendered together.
 */
export const trackResolvedState = (surface, options) => {
  // Destructure defensively, not in the signature: a `= {}` default only covers
  // `undefined`, so `trackResolvedState(s, null)` threw a TypeError — from the
  // convenience wrapper callers are steered toward, in a module whose header
  // declares NEVER-THROWS load-bearing.
  const opts = options || {};
  const { settled, errorSettled = settled, loading, error, isEmpty, disabled, reason } = opts;

  const flags = {
    settled,
    errorSettled,
    loading,
    error,
    isEmpty,
    disabled,
    wasError: lastReportedState(surface) === SURFACE_STATE.ERROR,
  };
  const rule = resolveSurfaceState(flags);
  if (!rule.state) return false;

  const resolvedReason = reason || rule.reason;
  return trackSurfaceState(surface, rule.state, resolvedReason ? { reason: resolvedReason } : {});
};

/** Stable surface ids. Add here rather than passing literals at call sites, so
 *  the set of instrumented surfaces is enumerable in one place. */
export const SURFACE = Object.freeze({
  REVIEW_QUEUE: 'review_queue',
});

/*
 * Quiz configuration abandoned — the classic drop-off on a dense config screen.
 *
 * Create Quiz asks for exam type, categories, a length and a name before it will
 * do anything. Every step is a place to give up, and today giving up is
 * completely silent: the funnel shows a student who arrived and no quiz, with no
 * way to tell whether they bounced instantly or filled in four fields and stalled
 * on the fifth. Those need opposite fixes.
 *
 * `step` is the FURTHEST point reached, so the distribution answers "where does
 * it break" rather than merely "how often". Reported once per abandonment.
 *
 * Only fires when the builder was actually TOUCHED. Arriving and leaving is a
 * navigation, not an abandonment, and counting it would swamp the signal with
 * people who opened the wrong tab.
 */
export const QUIZ_CONFIG_ABANDONED_EVENT = 'quiz_config_abandoned';

/** Furthest point reached in the builder, coarsest first. */
export const QUIZ_CONFIG_STEP = Object.freeze({
  /** Touched something, but no exam type chosen yet. */
  STARTED: 'started',
  EXAM_TYPE: 'exam_type_selected',
  CATEGORIES: 'categories_selected',
  /** The create button was live and they still left. NOTE: naming is NOT part
   *  of this ladder — see `named`, which travels as its own field. Folding it in
   *  made `ready` mean "renamed the quiz", since the name is auto-seeded and
   *  renaming is optional, which drained the highest-intent cohort into
   *  `exam_type_selected` alongside the instant bounces. */
  READY: 'ready',
});

/**
 * Pure: derive the furthest step from the flags the builder already computes, so
 * the caller does not invent its own ordering.
 */
export const resolveQuizConfigStep = ({ hasExamType, hasCategories, isReady } = {}) => {
  if (isReady) return QUIZ_CONFIG_STEP.READY;
  if (hasCategories) return QUIZ_CONFIG_STEP.CATEGORIES;
  if (hasExamType) return QUIZ_CONFIG_STEP.EXAM_TYPE;
  return QUIZ_CONFIG_STEP.STARTED;
};

let abandonmentReported = false;

/**
 * Called when the builder MOUNTS. The latch exists to collapse one departure
 * (which fires both unmount and pagehide) into one event — it is not meant to
 * silence a later, genuinely separate abandonment. Left per-page it did exactly
 * that: a second visit to the builder in the same session emitted nothing, and
 * the step distribution skewed toward the first (typically shallowest) attempt.
 * Scope is therefore per MOUNT, reset here.
 */
export const beginQuizConfigSession = () => { abandonmentReported = false; };

/** Test seam — module state has to be resettable between cases. */
export const __resetQuizConfigAbandoned = () => { abandonmentReported = false; };

/**
 * @param {object} params
 * @param {boolean} params.touched Did the student interact with the builder at all?
 * @param {boolean} params.launched Did a quiz actually get created?
 * @param {string} params.step One of QUIZ_CONFIG_STEP.
 * @returns {boolean} true when an event was emitted; never throws.
 */
export const trackQuizConfigAbandoned = ({ touched, launched, step, named } = {}) => {
  try {
    if (!touched || launched) return false;
    // Unmount and pagehide can both fire for one departure; one abandonment is
    // one event or the drop-off rate is inflated by however many exit paths the
    // browser happens to take.
    if (abandonmentReported) return false;
    abandonmentReported = true;

    // `named` is ORTHOGONAL to the step, not a rung on it: a student can name the
    // quiz first or last, so making it a precondition for `ready` conflated
    // intent with sequence.
    const payload = { step: step || QUIZ_CONFIG_STEP.STARTED };
    if (typeof named === 'boolean') payload.named = named;
    gtmTrack(QUIZ_CONFIG_ABANDONED_EVENT, { event_category: 'Diagnostics', ...payload });
    mixpanel.track(QUIZ_CONFIG_ABANDONED_EVENT, payload);
    return true;
  } catch (error) {
    noteSinkFailure('Quiz-config abandonment tracking', error);
    return false;
  }
};

/*
 * Explanation opened — the best available proxy for perceived content quality.
 *
 * Whether a student OPENS the explanation after answering, and whether they open
 * it after getting the question RIGHT (curiosity) or WRONG (repair), says more
 * about whether the bank is worth paying for than any completion count. A bank
 * whose explanations go unread is a bank with a content problem that every
 * success metric will happily hide.
 *
 * Emitted once per question per surface for the lifetime of the PAGE (module
 * state, not per drawer mount) — reopening the same explanation while working
 * through one question is one read, not five. A genuine second read in a later
 * quiz within the same page session is therefore not counted; deliberate, and
 * stated here because the previous wording said "per drawer session".
 *
 * questionId travels because the whole point is per-question: it is an opaque id,
 * not content and not PII, and it is what makes "which explanations get skipped"
 * answerable. No stem, no answer text, no user input — same posture as the
 * session-replay suppression on these surfaces.
 */
export const EXPLANATION_OPENED_EVENT = 'explanation_opened';

const openedExplanations = new Set();

/** Test seam — module state has to be resettable between cases. */
export const __resetExplanationOpened = () => openedExplanations.clear();

/**
 * @param {object} params
 * @param {string} params.questionId Opaque question id.
 * @param {string} [params.surface] Where it was opened from, e.g. 'exam_review_drawer'.
 * @param {boolean} [params.answeredCorrectly] Curiosity vs repair, when known.
 * @returns {boolean} true when an event was emitted; never throws.
 */
/*
 * HONEST SCOPE for `autoOpened`, because the dimension does not do what it was
 * introduced to do.
 *
 * On the TUTOR path it is effectively always true: the container force-opens the
 * drawer on answer and sets the flag unconditionally, and the per-question
 * dedupe latch then swallows a later deliberate re-open of the SAME question. So
 * `false` is reachable only for a review-mode student who closed the drawer
 * BEFORE reaching a question and then opened it.
 *
 * The dimension was added to defuse saturation — "the drawer opens by itself in
 * tutor mode, so a raw open-rate would sit near 100%". It does not: the
 * numerator still sits near 100% autoOpened:true, and looks identical for a bank
 * with excellent explanations and one with none. It labels the saturation rather
 * than separating it.
 *
 * Read it as a review-mode signal, not as a curiosity measure. The real
 * follow-up is a signal that survives the auto-open — dwell on the explanation
 * tab, or expansion of additionalExplanation — and is filed rather than implied.
 */
export const trackExplanationOpened = ({ questionId, surface, answeredCorrectly, autoOpened } = {}) => {
  try {
    // Coerce rather than reject. The codebase treats question ids as
    // possibly-numeric (exam-question-container defensively stringifies them),
    // so a strict string check made the instrument DEAD on any surface passing a
    // number — silently, with nothing to indicate it. Empty/nullish still fails
    // closed: the metric is per question or it is nothing.
    const id = questionId == null ? '' : String(questionId);
    if (!id) return false;
    const key = `${surface || 'unknown'}:${id}`;
    if (openedExplanations.has(key)) return false;
    openedExplanations.add(key);

    const payload = { questionId: id, ...(surface ? { surface } : {}) };
    if (typeof answeredCorrectly === 'boolean') payload.answeredCorrectly = answeredCorrectly;
    // Whether the surface opened itself. On the dominant path (tutor mode, the
    // default) the drawer auto-opens on answer, so without this dimension the
    // numerator mixes real reads with auto-opens and cannot be split afterwards.
    if (typeof autoOpened === 'boolean') payload.autoOpened = autoOpened;

    gtmTrack(EXPLANATION_OPENED_EVENT, { event_category: 'Diagnostics', ...payload });
    mixpanel.track(EXPLANATION_OPENED_EVENT, payload);
    return true;
  } catch (error) {
    // Same NEVER THROWS constraint as trackSurfaceState: an instrument must not
    // be able to break the surface it measures.
    // Through noteSinkFailure, not a bare console.debug. An ad-blocked Mixpanel
    // is a NORMAL production state, and this path would otherwise print one line
    // per question per review block — into a student's console and every CI run.
    // The keyed latch exists for exactly that, the sibling event already uses
    // it, and the inconsistency was mine.
    noteSinkFailure('Explanation-opened tracking', error);
    return false;
  }
};
